import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useSWR from 'swr';
import {
  ErrorMessage,
  Layout,
  SubmitButton,
  MetadataEditor
} from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { pricesApi, doctorsApi } from '@/shared/api';
import { priceItemSchema, type PriceItemFormData } from '@/shared/schemas';

/**
 * Страница создания услуги
 * Форма для добавления новой услуги с валидацией
 *
 * Включает следующие поля:
 * - name: Название услуги (обязательное)
 * - description: Описание услуги (опциональное, до 1000 символов)
 * - categoryId: Категория услуги (обязательное)
 * - doctorId: Привязка к врачу (опциональное)
 * - priceOffline: Цена очной консультации (опциональное)
 * - priceOnline: Цена онлайн консультации (опциональное)
 * - meta: Дополнительные метаданные (опциональное)
 * - isActive: Статус активности (по умолчанию true)
 * - sortOrder: Порядок сортировки (по умолчанию 0)
 *
 * @returns JSX элемент страницы создания услуги
 */
export const PriceItemCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Получаем categoryId из состояния навигации, если он был передан
  const preselectedCategoryId = location.state?.categoryId;

  // Загрузка категорий
  const { data: categoriesResponse, error: categoriesError } = useSWR(
    'price-categories-for-select',
    () => pricesApi.getAllCategories({ includeInactive: false }),
    { revalidateOnFocus: false }
  );

  // Загрузка врачей
  const { data: doctorsResponse, error: doctorsError } = useSWR(
    'doctors-for-select',
    () => doctorsApi.getAll({ includeInactive: false }),
    { revalidateOnFocus: false }
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    control
  } = useForm<PriceItemFormData>({
    resolver: zodResolver(priceItemSchema),
    defaultValues: {
      categoryId: preselectedCategoryId || 0,
      doctorId: null,
      name: '',
      description: null,
      priceOffline: null,
      priceOnline: null,
      meta: null,
      isActive: true,
      sortOrder: 0
    }
  });

  // Устанавливаем предвыбранную категорию при изменении
  useEffect(() => {
    if (preselectedCategoryId) {
      setValue('categoryId', preselectedCategoryId);
    }
  }, [preselectedCategoryId, setValue]);

  const categories = categoriesResponse?.categories || [];
  const doctors = doctorsResponse?.doctors || [];

  /**
   * Хук для управления отправкой формы с уведомлениями
   */
  const { isSubmitting: isFormSubmitting, handleSubmit: handleFormSubmit } =
    useFormSubmission<PriceItemFormData>({
      onSubmit: async (data: PriceItemFormData) => {
        // Преобразуем данные для API, правильно обрабатывая опциональные свойства
        const apiData = {
          categoryId: data.categoryId,
          name: data.name,
          isActive: data.isActive,
          sortOrder: data.sortOrder,
          // Convert undefined to null for API compatibility
          doctorId: data.doctorId ?? null,
          // Convert undefined to null for API compatibility
          meta: data.meta ?? null,
          ...(data.description && { description: data.description }),
          ...(data.priceOffline && { priceOffline: String(data.priceOffline) }),
          ...(data.priceOnline && { priceOnline: String(data.priceOnline) })
        };

        const response = await pricesApi.createItem(apiData);

        if (response.success) {
          // Перенаправляем на страницу цен через 2 секунды
          setTimeout(() => {
            navigate('/prices');
          }, 2000);
        }
      },
      successMessage: 'Услуга успешно создана',
      errorMessage: 'Ошибка при создании услуги'
    });

  /**
   * Обработчик отмены
   */
  const handleCancel = (): void => {
    navigate('/prices');
  };

  // Ошибка загрузки данных
  if (categoriesError || doctorsError) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Ошибка загрузки данных. Попробуйте обновить страницу." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Добавить услугу">
      <div className="space-y-6">
        {/* Заголовок */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-base-content">
              Добавить услугу
            </h2>
            <p className="text-base-content/60 mt-1">
              Создание новой услуги в прайс-листе клиники
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={isSubmitting || isFormSubmitting}
          >
            <i className="fas fa-arrow-left"></i>
            Назад к списку
          </button>
        </div>

        {/* Форма */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              {/* Название */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Название услуги <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className={`input input-bordered w-full ${errors.name ? 'input-error' : ''}`}
                  placeholder="Например: Первичная консультация педиатра"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Описание */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Описание услуги
                  </span>
                </label>
                <textarea
                  {...register('description')}
                  className={`textarea textarea-bordered w-full ${errors.description ? 'textarea-error' : ''}`}
                  placeholder="Подробное описание услуги, особенности проведения, что включает в себя консультация..."
                  rows={4}
                  disabled={isSubmitting}
                />
                {errors.description && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.description.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Опциональное поле. Максимум 1000 символов
                  </span>
                </label>
              </div>

              {/* Категория */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Категория <span className="text-error">*</span>
                  </span>
                </label>
                <select
                  {...register('categoryId', { valueAsNumber: true })}
                  className={`select select-bordered w-full ${errors.categoryId ? 'select-error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value={0}>Выберите категорию</option>
                  {categories.map((category: { id: number; name: string }) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.categoryId.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Врач */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Врач</span>
                </label>
                <select
                  {...register('doctorId', {
                    setValueAs: (value) => {
                      // Handle empty string, '0', or null/undefined values
                      if (
                        value === '' ||
                        value === '0' ||
                        value === null ||
                        value === undefined
                      ) {
                        return null;
                      }
                      // Parse the value as integer
                      const parsed = parseInt(value, 10);
                      // Return null if parsing results in NaN
                      return isNaN(parsed) ? null : parsed;
                    }
                  })}
                  className={`select select-bordered w-full ${errors.doctorId ? 'select-error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="">Не указан</option>
                  {doctors.map(
                    (doctor: {
                      id: number;
                      fullName: string;
                      specialization: string;
                    }) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.fullName} - {doctor.specialization}
                      </option>
                    )
                  )}
                </select>
                {errors.doctorId && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.doctorId.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Оставьте пустым, если услуга не привязана к конкретному
                    врачу
                  </span>
                </label>
              </div>

              {/* Цены */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Цена очной консультации */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Цена очной консультации (₽)
                    </span>
                  </label>
                  <input
                    type="string"
                    {...register('priceOffline')}
                    className={`input input-bordered w-full ${errors.priceOffline ? 'input-error' : ''}`}
                    placeholder="2000"
                    min="0"
                    disabled={isSubmitting}
                  />
                  {errors.priceOffline && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.priceOffline.message}
                      </span>
                    </label>
                  )}
                </div>

                {/* Цена онлайн консультации */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">
                      Цена онлайн консультации (₽)
                    </span>
                  </label>
                  <input
                    type="string"
                    {...register('priceOnline')}
                    className={`input input-bordered w-full ${errors.priceOnline ? 'input-error' : ''}`}
                    placeholder="1500"
                    min="0"
                    disabled={isSubmitting}
                  />
                  {errors.priceOnline && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.priceOnline.message}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Метаданные */}
              <MetadataEditor
                control={control}
                name="meta"
                title="Дополнительные атрибуты"
                description="Пользовательские метаданные для услуги в формате ключ-значение"
                disabled={isSubmitting}
              />

              {/* Порядок сортировки */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Порядок сортировки
                  </span>
                </label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className={`input input-bordered w-full ${errors.sortOrder ? 'input-error' : ''}`}
                  placeholder="0"
                  min="0"
                  disabled={isSubmitting}
                />
                {errors.sortOrder && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.sortOrder.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Чем меньше число, тем выше в списке будет отображаться
                    услуга
                  </span>
                </label>
              </div>

              {/* Статус активности */}
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="checkbox checkbox-primary"
                    disabled={isSubmitting}
                  />
                  <span className="label-text font-medium">
                    Активная услуга
                  </span>
                </label>
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Неактивные услуги не отображаются на сайте
                  </span>
                </label>
              </div>

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <SubmitButton
                  isLoading={isSubmitting || isFormSubmitting}
                  loadingText="Создание..."
                  icon="fas fa-save"
                  className="flex-1 sm:flex-none"
                >
                  Создать услугу
                </SubmitButton>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary flex-1 sm:flex-none"
                  disabled={isSubmitting || isFormSubmitting}
                >
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
